// src/components/headerone/bots/OmniBot2Demo.js
// Omnibot 2.0 Demo - uses demo deployment & host
import React, { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const DEPLOYMENT_KEY = 'dep_web_Ny03YNsY8W0L6wFmW9ZJI7nNFFidXL6P';

// Global flags to track script load / initialization (demo variant)
if (typeof window !== 'undefined') {
  if (typeof window.__omnibot2DemoScriptLoaded === 'undefined') {
    window.__omnibot2DemoScriptLoaded = false;
  }
  if (typeof window.__omnibot2DemoInitialized === 'undefined') {
    window.__omnibot2DemoInitialized = false;
  }
}

function OmniBot2Demo({ userData, payload: cognitoPayload }) {
  const location = useLocation();

  const getFullContextPayload = useCallback(() => {
    const w = typeof window !== 'undefined' ? window : null;
    const d = typeof document !== 'undefined' ? document : null;
    const page_url = w ? w.location.href : '';
    const page_title = d ? (d.title || '') : '';
    const pathname = location.pathname || '';
    const search = location.search || '';
    const hash = location.hash || '';

    const ctx = {
      page_label: 'General FAQ',
      page_url,
      page_title,
      pathname,
      search,
      hash,
      referrer: d?.referrer || '',
      language: w?.navigator?.language || '',
      languages: w?.navigator?.languages ? String(w.navigator.languages) : '',
      timezone: typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : '',
      screen_width: w?.screen?.width ?? '',
      screen_height: w?.screen?.height ?? '',
      context_updated_at: new Date().toISOString(),
    };

    if (userData) {
      if (userData.userId != null && userData.userId !== '') ctx.user_id_full = userData.userId;
      if (userData.customerID != null && userData.customerID !== '') ctx.user_id = userData.customerID;
      if (userData.fullName != null && userData.fullName !== '') ctx.user_name = userData.fullName;
      if (userData.firstName != null && userData.firstName !== '') ctx.first_name = userData.firstName;
      if (userData.lastName != null && userData.lastName !== '') ctx.last_name = userData.lastName;
      if (userData.emailAddress != null && userData.emailAddress !== '') ctx.user_email = userData.emailAddress;
      if (userData.accountNumber != null && userData.accountNumber !== '') ctx.account_number = userData.accountNumber;
    }

    // Documented context field: user_tier (subscription/tier for routing and dashboard)
    if (cognitoPayload && typeof cognitoPayload === 'object' && cognitoPayload['custom:user_tier'] != null) {
      ctx.user_tier = cognitoPayload['custom:user_tier'];
    } else if (userData?.userTier != null && userData.userTier !== '') {
      ctx.user_tier = userData.userTier;
    } else {
      ctx.user_tier = '';
    }

    if (cognitoPayload && typeof cognitoPayload === 'object') {
      if (cognitoPayload['cognito:username']) ctx.cognito_username = cognitoPayload['cognito:username'];
      if (cognitoPayload['custom:sms_optin'] != null) ctx.sms_optin = cognitoPayload['custom:sms_optin'];
    }

    return ctx;
  }, [location.pathname, location.search, location.hash, userData, cognitoPayload]);

  useEffect(() => {
    if (typeof document !== 'undefined' && document.body) {
      document.body.dataset.gtsActiveBot = 'omni2demo';
    }
  }, []);

  useEffect(() => {
    if (typeof document !== 'undefined' && document.body) {
      document.body.dataset.gtsActiveBot = 'omni2demo';
    }
  }, [location.pathname, location.search]);

  // Initialize widget once for demo deployment
  useEffect(() => {
    if (window.__omnibot2DemoScriptLoaded && window.__omnibot2DemoInitialized) {
      return;
    }

    const cssLinkId = 'omnibot2-demo-widget-css';
    if (!document.getElementById(cssLinkId)) {
      const link = document.createElement('link');
      link.id = cssLinkId;
      link.rel = 'stylesheet';
      link.href = 'https://widget.demo.dev.omnibots.ai/widget.css';
      document.head.appendChild(link);
    }

    const scriptId = 'omnibot2-demo-widget-js';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://widget.demo.dev.omnibots.ai/widget.iife.js';
      script.async = true;
      script.onload = () => {
        window.__omnibot2DemoScriptLoaded = true;
        if (window.__omnibot2DemoInitialized) return;

        const meta = getFullContextPayload();
        if (window.omnibot && typeof window.omnibot.init === 'function') {
          try {
            window.omnibot.init({
              deploymentKey: DEPLOYMENT_KEY,
              position: 'bottom-left',
              autoOpen: false,
              offset: { x: 20, y: 20 },
              zIndex: 9999,
              metadata: meta,
            });
            window.__omnibot2DemoInitialized = true;
            console.log('[OMNI 2.0 DEMO] Bot initialized (omnibot) – full payload sent:', meta);
            if (typeof window.omnibot.on === 'function') {
              window.omnibot.on('ready', () => {
                if (typeof window.omnibot.setContext === 'function') {
                  const payload = getFullContextPayload();
                  window.omnibot.setContext(payload);
                  console.log('[OMNI 2.0 DEMO] Context sent on ready – full payload:', payload);
                }
              });
            }
          } catch (e) {
            console.error('[OMNI 2.0 DEMO] Init error:', e);
          }
        }
      };
      script.onerror = () => console.error('[OMNI 2.0 DEMO] Failed to load script');
      document.body.appendChild(script);
    }
  }, [getFullContextPayload]);

  // Push full context on SPA route changes and when user context changes (e.g. after login)
  useEffect(() => {
    if (!window.__omnibot2DemoScriptLoaded || !window.__omnibot2DemoInitialized) return;

    const payload = getFullContextPayload();

    if (window.omnibot && typeof window.omnibot.setContext === 'function') {
      try {
        window.omnibot.setContext(payload);
        console.log('[OMNI 2.0 DEMO] Context updated (omnibot.setContext) – full payload sent:', payload);
      } catch (_) {}
    }
  }, [location.pathname, location.search, location.hash, getFullContextPayload]);

  return null;
}

export default OmniBot2Demo;

